/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable no-useless-escape */
import React, { useEffect, useState } from 'react';
import { Grid, Paper, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useToast } from 'src/context/ToastContext';
import PasswordChecklist from 'react-password-checklist';
import END_POINT from '../../config/endpoints.constants';
import UserLayout from '../UserLayout';
import SEO from '../common/SEO';
import { SITE_DESCRIPTION } from '../../config/seo.constants';
import withAuth from '../../hoc/withAuth';
import useCustomerCallRedirect from '../../hooks/useCustomerCallRedirect';
import useLogout from '../../hooks/useLogout';
import Button from '../common/Button';
import PasswordField from '../common/PasswordField';
import regionConfig from '../../config';
import {
    PASSWORD_CHECKLIST_RULES,
    PASSWORD_MAX_LENGTH,
    PASSWORD_MIN_LENGTH,
    isPasswordValid,
} from '../../validation/password.schema';

const paperSx = {
    marginTop: '3rem',
    display: 'flex',
    justifyContent: 'space-between',
    gap: '24px',
};

const titleSx = {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '40px',
    marginBottom: '50px',
    color: 'black',
};

const btnContainerSx = {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '25px',
};

const confirmTextSx = {
    display: 'flex',
    justifyContent: 'center',
    color: 'red',
};

const successPaperSx = (theme: any) => ({
    padding: theme.spacing(5),
    margin: 'auto',
    marginTop: '20px',
    width: '85%',
});

const ChangePasswordSetting = () => {
    useCustomerCallRedirect();
    const toast = useToast();
    const navigate = useNavigate();
    const [changePasswordDetails, setChangePasswordDetails] = useState({
        oldPassword: '',
        newPassword: '',
        repeatNewPassword: '',
        isPasswordModified: 'no',
        error: '',
        passwordMisMatch: false,
    });
    const [isButtonEnabled, setIsButtonEnabled] = useState(false);
    const logout = useLogout();

    const handleSubmit = async (e: any) => {
        e.preventDefault();
        const { oldPassword, newPassword } = changePasswordDetails;
        if (oldPassword === newPassword) {
            toast.error('Current password and new password can not be same');
            return;
        }
        const req = {
            oldPassword,
            newPassword,
        };
        try {
            const res = await axios.post(END_POINT.RESETPWD, req);
            if (res.data.code === 0) {
                setChangePasswordDetails({ ...changePasswordDetails, isPasswordModified: 'yes' });
                await logout(false);
            }
        } catch (err) {
            const { code, message } = (err as any).response.data;
            if (code === 1) {
                toast.error(message);
            }
        }
    };

    const handleChange = (e: any) => {
        const { name, value } = e.target;
        setChangePasswordDetails({ ...changePasswordDetails, [name]: value });
    };

    const redirectToLogin = () => {
        navigate('/login');
    };

    useEffect(() => {
        setIsButtonEnabled(
            isPasswordValid(changePasswordDetails.newPassword) &&
                changePasswordDetails.newPassword === changePasswordDetails.repeatNewPassword,
        );
    }, [changePasswordDetails]);

    return (
        <UserLayout>
            <SEO
                title="Change Password Setting"
                description={`${regionConfig.appName} Change Password Setting - ${SITE_DESCRIPTION}`}
            />
            {changePasswordDetails.isPasswordModified === 'no' ? (
                <Grid container justifyContent="center">
                    <Paper sx={paperSx}>
                        <div style={{ padding: 40, margin: 'auto' } as any}>
                            <h4 className="text-center mb-8">Change Your Password</h4>
                            <form>
                                <PasswordField
                                    label="Old Password"
                                    placeholder="Enter Old Password"
                                    allowPasswordToggle
                                    required
                                    fullWidth
                                    type="password"
                                    name="oldPassword"
                                    onChange={handleChange}
                                    aria-label="Old Password"
                                    ariaLabel="Old Password"
                                />
                                <PasswordField
                                    label="New Password"
                                    placeholder="Enter New Password"
                                    allowPasswordToggle
                                    required
                                    fullWidth
                                    type="password"
                                    name="newPassword"
                                    autoComplete="new-password"
                                    onChange={handleChange}
                                    aria-label="New Password"
                                    ariaLabel="New Password"
                                />
                                <PasswordField
                                    label="Repeat New Password"
                                    placeholder="Repeat New Password"
                                    allowPasswordToggle
                                    required
                                    fullWidth
                                    type="password"
                                    name="repeatNewPassword"
                                    autoComplete="new-password"
                                    onChange={handleChange}
                                    aria-label="Repeat Password"
                                    ariaLabel="Repeat Password"
                                />
                                <Button
                                    onClick={handleSubmit}
                                    aria-label="Update Password"
                                    id="verifyProfile"
                                    fullWidth
                                    size="large"
                                    disabled={!isButtonEnabled}
                                >
                                    Update Password
                                </Button>
                            </form>
                        </div>
                        <div style={{ padding: 40, margin: 'auto', marginTop: '4rem' } as any}>
                            <p>Password Requirements :</p>
                            <PasswordChecklist
                                rules={PASSWORD_CHECKLIST_RULES}
                                minLength={PASSWORD_MIN_LENGTH}
                                maxLength={PASSWORD_MAX_LENGTH}
                                value={changePasswordDetails.newPassword}
                                valueAgain={changePasswordDetails.repeatNewPassword}
                                messages={{
                                    minLength: 'Must have at least 10 characters',
                                    maxLength: 'Must be less than 30 characters',
                                    capital: 'Must contain atleast one uppercase letter',
                                    number: 'Must contain at least one number',
                                    match: 'New Password and Repeat New Password match',
                                }}
                                validTextColor="#000"
                                invalidTextColor="#000"
                            />
                        </div>
                    </Paper>
                </Grid>
            ) : (
                <Grid container justifyContent="center">
                    <Grid size={{ xs: 12 }}>
                        <Typography sx={titleSx} variant="h4">
                            {regionConfig.appName} Admin
                        </Typography>
                    </Grid>
                    <form>
                        <Paper sx={successPaperSx}>
                            <Grid container>
                                <Grid sx={{ justifyContent: 'center' }} size={{ xs: 12 }}>
                                    <Typography sx={confirmTextSx} variant="h6">
                                        Success! You can now login to {regionConfig.appName} with
                                        your new password
                                    </Typography>
                                </Grid>
                                <Grid
                                    sx={{ ...btnContainerSx, justifyContent: 'center' }}
                                    size={{ xs: 12 }}
                                >
                                    <Button
                                        type="button"
                                        id="verifyProfile"
                                        onClick={redirectToLogin}
                                        aria-label="Go To Login"
                                    >
                                        Go To Login
                                    </Button>
                                </Grid>
                            </Grid>
                        </Paper>
                    </form>
                </Grid>
            )}
        </UserLayout>
    );
};

export default withAuth(ChangePasswordSetting);
